import { inject } from '@angular/core';
import type { CanActivateChildFn, CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { AuthService } from 'app/core/auth/auth.service';
import { consumeForceInteractiveLoginCookie } from 'app/core/auth/post-logout-redirect.util';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';

export const AuthGuard: CanActivateFn | CanActivateChildFn = async (_, state) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const msalService = inject(MsalService);
  const msalBroadcast = inject(MsalBroadcastService);

  console.log('[AuthGuard]', state.url);

  // ⚓ If we landed with an MSAL response in the hash, defer to AuthService (single handler)
  const hash = window.location.hash || '';
  if (hash.includes('code=') || hash.includes('id_token=') || hash.includes('error=')) {
    console.log('[AuthGuard] MSAL hash detected; deferring to AuthService handler.');
    return false;
  }

  // 🧮 Track redirect attempts and allow persisting a session-level max attempts
  const redirectCount = Number(sessionStorage.getItem('msal_redirect_count') || '0');
  const maxAttempts = (() => {
    const stored = sessionStorage.getItem('msal_max_redirect_attempts');
    const parsed = stored ? Number(stored) : NaN;
    if (!Number.isFinite(parsed) || parsed <= 0) {
      sessionStorage.setItem('msal_max_redirect_attempts', '3');
      return 3;
    }
    return parsed;
  })();

  if (consumeForceInteractiveLoginCookie()) {
    sessionStorage.setItem('msal_redirect_count', String(redirectCount + 1));
    try {
      await firstValueFrom(
        msalService.loginRedirect({
          scopes: [...environment.basicScopes, ...environment.api.scopes],
          prompt: 'login',
          redirectStartPage: state.url,
        })
      );
    } catch (e) {
      console.error('[AuthGuard] Forced login redirect failed:', e);
      sessionStorage.setItem('auth_error', '1');
      await router.navigate(['/auth-fail'], { queryParams: { description: 'Redirect failed' } });
    }
    return false;
  }

  // ✅ Already authenticated?
  const isAuth = await firstValueFrom(authService.check());
  if (isAuth) {
    console.log('[AuthGuard] User authenticated');
    sessionStorage.removeItem('msal_redirect_count');
    sessionStorage.removeItem('auth_error');
    return true;
  }

  // 🕓 Wait until no MSAL interaction is in progress
  const interactionStatus = await firstValueFrom(
    msalBroadcast.inProgress$.pipe(
      filter((status) => status === InteractionStatus.None),
      take(1)
    )
  );

  console.log('[AuthGuard] Interaction status:', interactionStatus);

  // 👤 Try to reuse an existing account
  const account = msalService.instance.getActiveAccount() || msalService.instance.getAllAccounts()[0];

  if (account) {
    try {
      console.log('[AuthGuard] Acquiring silent token...');
      await msalService.acquireTokenSilent({
        scopes: [...environment.basicScopes, ...environment.api.scopes],
        account,
      });
      console.log('[AuthGuard] Silent token acquired successfully');
      sessionStorage.removeItem('msal_redirect_count');
      sessionStorage.removeItem('auth_error');
      return true;
    } catch (err) {
      const msg = err?.message?.toLowerCase?.() || '';
      const interactionRequired =
        msg.includes('interaction_required') || msg.includes('login_required') || msg.includes('consent_required');
      console.error('[AuthGuard] Silent token failed:', err);
      if (interactionRequired && redirectCount < maxAttempts) {
        console.log(`[AuthGuard] Starting login redirect (attempt #${redirectCount + 1})`);
        sessionStorage.setItem('msal_redirect_count', String(redirectCount + 1));
        try {
          await firstValueFrom(
            msalService.loginRedirect({
              scopes: [...environment.basicScopes, ...environment.api.scopes],
              redirectStartPage: state.url,
            })
          );
        } catch (e) {
          console.error('[AuthGuard] Redirect failed:', e);
          sessionStorage.setItem('auth_error', '1');
          await router.navigate(['/auth-fail'], { queryParams: { description: 'Redirect failed' } });
        }
        return false;
      }
      sessionStorage.setItem('auth_error', '1');
      await router.navigate(['/auth-fail'], { queryParams: { description: 'Silent token acquisition failed' } });
      return false;
    }
  }

  // 🚀 Trigger login redirect if no account found
  if (redirectCount >= maxAttempts) {
    console.warn('[AuthGuard] Too many authentication attempts');
    await router.navigate(['/auth-fail'], {
      queryParams: {
        description: 'Too many authentication attempts. Please try again later.',
      },
    });
    return false;
  }

  console.log(`[AuthGuard] Redirecting to login (attempt #${redirectCount + 1})`);
  sessionStorage.setItem('msal_redirect_count', String(redirectCount + 1));

  try {
    await firstValueFrom(
      msalService.loginRedirect({
        scopes: [...environment.basicScopes, ...environment.api.scopes],
        redirectStartPage: state.url,
      })
    );
  } catch (err) {
    console.error('[AuthGuard] Redirect failed:', err);
    sessionStorage.setItem('auth_error', '1');
    await router.navigate(['/auth-fail'], { queryParams: { description: 'Redirect failed' } });
  }

  return false;
};
